/**
 * WorkspaceTabs — per-page closeable tabs in the AppBar.
 *
 * Tab identity = route path. Each page the user opens (Cmd-click in the
 * sidebar) becomes its own tab. Regular clicks navigate within the
 * active tab.
 *
 * Behaviours:
 *   - Auto-open: if the user navigates to a path with no matching tab
 *     (e.g. via a deep link), a tab is created for it.
 *   - Click an existing tab → navigate to its path.
 *   - X on a tab → close it (unless it's Home).
 *   - Cmd/Ctrl-W (no Shift) → close the active tab.
 *   - "+" button → opens a menu of sections, pick a section to open its
 *     module-home in a new tab.
 *
 * Spec: 2026-05-19-qfinpricer-portal-skin-qyas.md, day-4 follow-up
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  useTheme,
} from '@mui/material';
import { Home as HomeIcon, X as CloseIcon, Plus as PlusIcon } from 'lucide-react';
import { navigationConfig, findSectionForPath } from './navigationConfig';
import { useWorkspaceTabs } from './workspaceTabsStore';
import { buildSectionTabForPath, buildPageTab } from './workspaceTabsHelpers';

export const WorkspaceTabs: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { tabs, activeId, open, close, setActive, updatePath } = useWorkspaceTabs();
  const [moreAnchor, setMoreAnchor] = React.useState<null | HTMLElement>(null);

  // ── Sync route → tabs ──────────────────────────────────────────────
  // Section-anchored model:
  //   - If active tab's path already matches → no-op.
  //   - Else resolve the route's owning section; if a section tab for it
  //     exists, just update its path (label stays the section's label) and
  //     activate. Otherwise open a fresh section tab.
  //   - Page tabs (Cmd-click only) are matched exactly by path before the
  //     section lookup, so an open page tab takes precedence over its
  //     section tab.
  useEffect(() => {
    const route = location.pathname;
    if (route === '/' || route === '/home') {
      if (activeId !== 'home') setActive('home');
      return;
    }
    // 1. Active tab already points at this route — nothing to do.
    const active = tabs.find((t) => t.id === activeId);
    if (active && active.path === route) return;

    // 2. Exact match against a PAGE tab (Cmd-opened) — activate it.
    const pageMatch = tabs.find((t) => t.kind === 'page' && t.path === route);
    if (pageMatch) {
      if (activeId !== pageMatch.id) setActive(pageMatch.id);
      return;
    }

    // 3. Look up the section that owns this route.
    const section = findSectionForPath(route);
    if (section) {
      const sectionTabId = `section:${section.id}`;
      const existing = tabs.find((t) => t.id === sectionTabId);
      if (existing) {
        updatePath(sectionTabId, route);
        if (activeId !== sectionTabId) setActive(sectionTabId);
        return;
      }
      open(buildSectionTabForPath(route));
      return;
    }

    // 4. Orphan route (no section) — fall back to a page tab.
    open(buildPageTab(route));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const activeIndex = Math.max(tabs.findIndex((t) => t.id === activeId), 0);

  // Cmd/Ctrl-W closes the active tab. Shift+W still closes the browser tab.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isCloseShortcut = (e.metaKey || e.ctrlKey) && !e.shiftKey && e.key.toLowerCase() === 'w';
      if (!isCloseShortcut) return;
      const active = tabs.find((t) => t.id === activeId);
      if (active?.closeable) {
        e.preventDefault();
        close(active.id);
        // setActive in close() picks the neighbouring tab — navigate to its path.
        // We can't read post-state synchronously, so navigate to '/' as a safe
        // fallback; the auto-sync useEffect below will re-converge.
        navigate('/');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tabs, activeId, close, navigate]);

  const handleTabChange = (_: React.SyntheticEvent, idx: number) => {
    const tab = tabs[idx];
    if (!tab) return;
    setActive(tab.id);
    if (location.pathname !== tab.path) navigate(tab.path);
  };

  const handleClose = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    const tab = tabs.find((t) => t.id === tabId);
    if (!tab?.closeable) return;
    const wasActive = tab.id === activeId;
    close(tab.id);
    if (wasActive) navigate('/');
  };

  const availableSections = navigationConfig.filter(
    (s) => !tabs.some((t) => t.id === `section:${s.id}`),
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: 40,
        bgcolor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 1,
      }}
    >
      <Tabs
        value={activeIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 40,
          flex: 1,
          '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main, height: 2 },
          '& .MuiTab-root': {
            minHeight: 40,
            py: 0,
            px: 1.5,
            textTransform: 'none',
            color: theme.palette.text.secondary,
            '&.Mui-selected': { color: theme.palette.text.primary },
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {tab.id === 'home' && <HomeIcon size={14} />}
                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                  {tab.label}
                </Typography>
                {tab.closeable && (
                  <Box
                    component="span"
                    onClick={(e) => handleClose(e, tab.id)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 0.25,
                      ml: 0.5,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    }}
                    role="button"
                    aria-label={`Close ${tab.label}`}
                  >
                    <CloseIcon size={12} />
                  </Box>
                )}
              </Box>
            }
          />
        ))}
      </Tabs>

      {availableSections.length > 0 && (
        <>
          <Tooltip title="Open another section">
            <IconButton
              size="small"
              onClick={(e) => setMoreAnchor(e.currentTarget)}
              sx={{ ml: 1 }}
            >
              <PlusIcon size={16} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={moreAnchor}
            open={Boolean(moreAnchor)}
            onClose={() => setMoreAnchor(null)}
            PaperProps={{ sx: { maxHeight: 400, width: 240 } }}
          >
            {availableSections.map((section) => {
              const path = '/' + section.id;
              return (
                <MenuItem
                  key={section.id}
                  onClick={() => {
                    open(buildSectionTabForPath(path));
                    navigate(path);
                    setMoreAnchor(null);
                  }}
                >
                  <ListItemIcon>{section.icon}</ListItemIcon>
                  <ListItemText>{section.label}</ListItemText>
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default WorkspaceTabs;
